/**
 * B2B billing store — plans catalog + current subscription + quota status.
 *
 * Separate from useB2BAuthStore: this only carries billing data. Hydrated via
 * explicit loadPlans() / loadSubscription() calls — not from localStorage,
 * because the quota mutates in real time as screenings are generated and stale
 * cached data would mislead the upgrade UX.
 */
import { create } from 'zustand';
import { api, APIError } from '../api/client';

export const useB2BBillingStore = create((set, get) => ({
  plans: [],
  subscription: null, // { id, plan: {...}, status, billing_period, expires_at, auto_renew, quota: {...} }
  loadingPlans: false,
  loadingSubscription: false,
  error: '',

  loadPlans: async () => {
    if (get().loadingPlans) return;
    set({ loadingPlans: true, error: '' });
    try {
      const data = await api.get('/api/b2b/billing/plans');
      set({ plans: Array.isArray(data) ? data : [], loadingPlans: false });
    } catch (err) {
      const msg = err instanceof APIError ? err.message : 'Could not load plans';
      set({ loadingPlans: false, error: msg });
    }
  },

  loadSubscription: async () => {
    if (get().loadingSubscription) return;
    set({ loadingSubscription: true, error: '' });
    try {
      const data = await api.get('/api/b2b/billing/subscription');
      set({ subscription: data, loadingSubscription: false });
    } catch (err) {
      const msg = err instanceof APIError ? err.message : 'Could not load subscription';
      set({ loadingSubscription: false, error: msg });
    }
  },

  cancelAutoRenew: async () => {
    try {
      await api.post('/api/b2b/billing/recurring/cancel', {});
      // Reload subscription to refresh auto_renew flag.
      await get().loadSubscription();
      return { ok: true };
    } catch (err) {
      const msg = err instanceof APIError ? (err.data?.detail || err.message) : 'Could not cancel auto-renewal';
      return { ok: false, error: typeof msg === 'string' ? msg : 'Could not cancel auto-renewal' };
    }
  },

  reset: () => set({ plans: [], subscription: null, error: '' }),
}));

export const selectQuota = (s) => s.subscription?.quota || null;
export const selectPlanCode = (s) => s.subscription?.plan?.plan_code || null;
